import {
  apiErrorMessage,
  entityLabel,
  resourceLabel,
  actionLabel,
  issuesOf,
  renderIssue,
} from 'helpers/serverIssues';
import i18n from 'i18n';
import { pushToast, updateToast, dismissToast, dismissAll, DURATIONS } from './toastStore';

/**
 * The imperative toast API. Signatures are byte-compatible with the
 * react-toastify era so the ~120 call sites did not have to move when the
 * engine was swapped (toast-modernization phase 1).
 */

/**
 * Enum for alert types to avoid hardcoding strings throughout the application.
 * @readonly
 * @enum {string}
 */
export const AlertType = Object.freeze({
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  /**
   * WARNING was used by 13 call sites for years while this enum did not define
   * it, so `notify(..., AlertType.WARNING)` fell through every branch and the
   * toast silently never rendered. It is a real variant now, and an unknown
   * type throws in dev rather than disappearing (T4).
   */
  WARNING: 'warning',
});

const VARIANTS = new Set(Object.values(AlertType));

const IS_PROD = typeof import.meta !== 'undefined' && import.meta.env?.PROD;

/**
 * Displays a toast notification with a title and message.
 *
 * @param {string} title
 * @param {string} message
 * @param {AlertType} type
 * @param {object} [options] `duration` (ms) and `action` are honoured;
 *   `autoClose` is accepted as the legacy spelling of `duration`; `position`
 *   and `modal` are dead options kept only so old call sites do not throw.
 * @returns {number|undefined} the toast id
 */
export const notify = (title, message, type, options = {}) => {
  const { autoClose, duration, position, modal, action, ...rest } = options;

  if (!IS_PROD && (position !== undefined || modal !== undefined)) {
    // eslint-disable-next-line no-console
    console.warn(
      `notify: "${position !== undefined ? 'position' : 'modal'}" is no longer supported ` +
        '(one viewport, no modal fallback) — drop it from the call site.',
      { title },
    );
  }
  if (!IS_PROD && Object.keys(rest).length > 0) {
    // eslint-disable-next-line no-console
    console.warn('notify: unknown options ignored', Object.keys(rest));
  }

  if (!VARIANTS.has(type)) {
    // Dev throws so a typo'd/missing variant is caught at the call site;
    // production degrades to `info` rather than dropping the message.
    if (!IS_PROD) {
      throw new Error(
        `notify: unknown AlertType "${String(type)}" — use AlertType.SUCCESS/ERROR/WARNING/INFO`,
      );
    }
    return pushToast({ variant: AlertType.INFO, title, description: message, action });
  }

  return pushToast({
    variant: type,
    title,
    description: message,
    duration: duration ?? autoClose ?? undefined,
    action,
  });
};

/**
 * Renders a backend failure. The *content* pipeline is untouched from the
 * react-toastify era (T1): title nouns come from the envelope's `entity`
 * (falling back to the legacy URL-guessed `resource`/`action`), and the body
 * prefers the translated `issues[]` over the frozen English `message`.
 */
export const notifyError = (err, options = {}) => {
  const error = err?.response?.data;
  if (typeof error === 'object' && error !== null) {
    const title = i18n.t('common:notify.resourceActionFailed', {
      // `entity` (phase 6) supersedes the URL-guessed `resource`; the
      // legacy noun stays as fallback until the backend drops the field.
      resource:
        (error.entity ? entityLabel(error.entity) : resourceLabel(error.resource)) ||
        i18n.t('common:notify.somethingFallback', 'Something'),
      action: actionLabel(error.action) || '',
      defaultValue: '{{resource}} {{action}} has failed',
    });

    // A validation failure with several field issues used to arrive as one
    // "field: sentence; field: sentence" run-on. Lead with the first and put
    // the rest behind an expander (T11). apiErrorMessage stays the joiner for
    // its other, non-toast consumers.
    const issues = issuesOf(err);
    if (issues.length > 1) {
      const lines = issues.map((issue) => {
        const text = renderIssue(issue);
        const field = Array.isArray(issue.path) && issue.path.length > 0 ? issue.path.at(-1) : null;
        return field ? `${field}: ${text}` : text;
      });
      const { autoClose, duration, action } = options;
      return pushToast({
        variant: AlertType.ERROR,
        title,
        description: lines[0],
        details: lines.slice(1),
        duration: duration ?? autoClose ?? undefined,
        action,
      });
    }

    return notify(
      title,
      // Translated issues[] first (serverErrors.zod.*); the raw English
      // envelope `message` is the older-backend fallback, never the API.
      apiErrorMessage(err) ?? error.message,
      AlertType.ERROR,
      options,
    );
  }
  return notify(
    i18n.t('common:notify.somethingWentWrong', 'Something went wrong'),
    i18n.t('common:notify.tryAgainLater', 'Try again later'),
    AlertType.ERROR,
    options,
  );
};

/**
 * The ergonomic surface. Prefer this over `notify`:
 *
 *   cgToast.success('Saved', { description: 'Diagram has been saved' })
 *   cgToast.error('Upload failed', { action: { label: 'Retry', onClick: retry } })
 *   cgToast.apiError(err)
 *
 * `opts`: { description, details, duration, action: { label, onClick } }.
 */
const variantFn =
  (variant) =>
  (title, opts = {}) =>
    pushToast({
      variant,
      title,
      description: opts.description,
      details: opts.details,
      duration: opts.duration,
      action: opts.action,
    });

export const cgToast = Object.freeze({
  success: variantFn(AlertType.SUCCESS),
  error: variantFn(AlertType.ERROR),
  warning: variantFn(AlertType.WARNING),
  info: variantFn(AlertType.INFO),
  /** Renders a backend failure envelope through the translated error voice. */
  apiError: (err, options) => notifyError(err, options),
  dismiss: dismissToast,
  dismissAll,
});

export { dismissToast, updateToast, dismissAll, DURATIONS };
